import _ from 'lodash';
import { useMemo, useState } from 'react';
import localEntitySearch from '../search/entitySearch/localEntitySearch';
import TopicSelectHierarchicalOption from './TopicSelectHierarchicalOption';
import useAllTopics from './useAllTopics';

export default ({
  components = {},
  isDisabled,
  tooltip,
  getExtra = undefined,
  getValue,
  currentValue,
  limit = 100,
  skip = false,
}) => {
  const [inputValue, setInputValue] = useState('');
  const allTopics = useAllTopics();
  const flattenAllTopics = useAllTopics({ flatten: true });

  const currentTopic =
    currentValue && flattenAllTopics.find((topic) => topic.id === currentValue);

  const allTopicsOptions = useMemo(() => {
    if (skip) {
      return [];
    }

    const currentTopicAncestorIds = new Set(
      currentTopic ? currentTopic.ancestors.map(({ id }) => id) : [],
    );

    const flattenAllTopicOptions = [];

    const fillOptions = (childTopics = [], ancestors = [], forced = false) => {
      if (flattenAllTopicOptions.length >= limit && !forced) {
        return;
      }

      _.sortBy(childTopics, [(topic) => (isDisabled(topic) ? 1 : -1)]).forEach(
        (topic) => {
          flattenAllTopicOptions.push({
            isDisabled: isDisabled(topic),
            tooltip: tooltip ? tooltip(topic) : undefined,
            topic: { ...topic, ancestors: topic.ancestors || ancestors },
            value: getValue ? getValue(topic) : topic,
          });

          fillOptions(
            topic.childTopics,
            [...ancestors, topic],
            currentTopicAncestorIds.has(topic.id),
          );
        },
      );
    };

    fillOptions(allTopics);

    if (flattenAllTopicOptions.length < flattenAllTopics.length) {
      flattenAllTopicOptions.push({
        __isLimited__: true,
        isDisabled: true,
      });
    }

    return flattenAllTopicOptions;
  }, [
    skip,
    allTopics,
    flattenAllTopics.length,
    isDisabled,
    tooltip,
    getValue,
    limit,
    currentTopic,
  ]);

  const searchResults = useMemo(() => {
    const config = {
      topic: {
        sortBy: (l, r) => {
          if (!inputValue) return -1;
          const isLeftDisabled = isDisabled(l);
          const isRightDisabled = isDisabled(r);

          if (isLeftDisabled === isRightDisabled) {
            return 0;
          }

          return isLeftDisabled ? 1 : -1;
        },
        extra: (topic) => ({
          isDisabled: isDisabled(topic),
          value: getValue ? getValue(topic) : topic,
          tooltip: tooltip ? tooltip(topic) : undefined,
          ...getExtra?.(topic),
        }),
        objects: flattenAllTopics,
      },
    };

    return !skip && inputValue
      ? localEntitySearch(inputValue, config, { limit })
      : [];
  }, [
    flattenAllTopics,
    getExtra,
    getValue,
    inputValue,
    isDisabled,
    limit,
    skip,
    tooltip,
  ]);

  const handleInputChange = (newInputValue, actionMeta) => {
    if (!['input-blur', 'menu-close'].includes(actionMeta.action)) {
      setInputValue(newInputValue);
    }
  };

  return {
    components: { Option: TopicSelectHierarchicalOption, ...components },
    filterOption: null,
    inputValue,
    onInputChange: handleInputChange,
    options: inputValue ? searchResults : allTopicsOptions,
  };
};
